(() => {
  'use strict';

  const hub = document.querySelector('[data-mattress-hub]');
  if (!hub) return;

  const all = (selector, context = hub) => Array.from(context.querySelectorAll(selector));

  const track = (eventName, details = {}) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, details);
      return;
    }
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...details });
  };

  const setupLinkTracking = () => {
    const heroLink = hub.querySelector('.mh-hero__actions .mh-btn--primary');
    if (heroLink) heroLink.addEventListener('click', () => track('bedra_mattress_flagship_clicked', { model_key: 'a11' }));

    all('[data-mh-model-link]').forEach((link) => {
      link.addEventListener('click', () => {
        const card = link.closest('[data-mh-model]');
        track('bedra_mattress_product_clicked', {
          model_key: card?.dataset.modelKey || '',
          model_name: card?.dataset.modelName || '',
          mattress_size: card?.querySelector('[data-mh-size-option][aria-pressed="true"]')?.dataset.size || ''
        });
      });
    });

    all('.mh-review-card > a').forEach((link) => {
      link.addEventListener('click', () => track('bedra_mattress_marketplace_source_clicked', { source_url: link.href }));
    });
  };

  const setupSizeSelection = () => {
    all('[data-mh-model]').forEach((card) => {
      const options = all('[data-mh-size-option]', card);
      const price = card.querySelector('[data-mh-price]');
      const priceLabel = card.querySelector('[data-mh-price-label]');
      const modelLink = card.querySelector('[data-mh-model-link]');
      if (!options.length || !price || !priceLabel || !modelLink) return;

      options.forEach((option) => {
        option.addEventListener('click', () => {
          if (option.disabled) return;
          options.forEach((item) => {
            const active = item === option;
            item.classList.toggle('is-selected', active);
            item.setAttribute('aria-pressed', String(active));
          });
          price.textContent = option.dataset.priceFormatted;
          priceLabel.textContent = option.dataset.size;
          modelLink.href = option.dataset.url;
          card.dataset.modelUrl = option.dataset.url;
          track('bedra_mattress_size_selected', {
            model_key: card.dataset.modelKey,
            model_name: card.dataset.modelName,
            mattress_size: option.dataset.size,
            value: Number(option.dataset.price || 0) / 100,
            currency: 'AUD'
          });
        });
      });
    });
  };

  const setupShortlist = () => {
    const shortlist = hub.querySelector('[data-mh-shortlist]');
    const summary = hub.querySelector('[data-mh-shortlist-summary]');
    const names = hub.querySelector('[data-mh-shortlist-names]');
    const status = hub.querySelector('[data-mh-shortlist-status]');
    const compare = hub.querySelector('[data-mh-shortlist-compare]');
    const clear = hub.querySelector('[data-mh-shortlist-clear]');
    const reset = hub.querySelector('[data-mh-compare-reset]');
    const compareTable = hub.querySelector('.mh-compare-table');
    const toggles = all('[data-mh-shortlist-toggle]');
    if (!shortlist || !summary || !names || !status || !compare || !clear || !reset || !toggles.length) return;

    const selected = new Map();
    const showAllModels = () => {
      all('[data-compare-model]').forEach((item) => item.classList.remove('is-shortlist-hidden'));
      compareTable?.classList.remove('is-filtered');
      reset.hidden = true;
    };

    const render = () => {
      const count = selected.size;
      summary.textContent = count === 0
        ? 'Select two or three mattresses to compare.'
        : `${count} mattress${count === 1 ? '' : 'es'} selected`;
      names.textContent = count ? Array.from(selected.values()).join(' · ') : 'No mattresses selected';
      compare.disabled = count < 2;
      clear.hidden = count === 0;
      toggles.forEach((button) => {
        const active = selected.has(button.dataset.modelKey);
        button.setAttribute('aria-pressed', String(active));
        button.disabled = count >= 3 && !active;
        const label = button.querySelector('[data-mh-shortlist-label]');
        const icon = button.querySelector('[aria-hidden="true"]');
        if (label) label.textContent = active ? 'Remove from shortlist' : 'Add to shortlist';
        if (icon) icon.textContent = active ? '✓' : '＋';
      });
      status.textContent = count === 3 ? 'Shortlist full. Remove one mattress to choose another.' : '';
    };

    toggles.forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.modelKey;
        if (selected.has(key)) selected.delete(key);
        else if (selected.size < 3) selected.set(key, button.dataset.modelName);
        showAllModels();
        render();
        track('bedra_mattress_shortlist_updated', {
          action: selected.has(key) ? 'added' : 'removed',
          model_key: key,
          shortlist_count: selected.size
        });
      });
    });

    compare.addEventListener('click', () => {
      if (selected.size < 2) {
        status.textContent = 'Select at least two mattresses to compare.';
        return;
      }
      all('[data-compare-model]').forEach((item) => {
        item.classList.toggle('is-shortlist-hidden', !selected.has(item.dataset.compareModel));
      });
      compareTable?.classList.add('is-filtered');
      reset.hidden = false;
      status.textContent = `Showing ${selected.size} selected mattresses in the comparison.`;
      track('bedra_mattress_shortlist_compared', {
        model_keys: Array.from(selected.keys()).join('|'),
        shortlist_count: selected.size
      });
      document.querySelector('#compare-mattresses')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    clear.addEventListener('click', () => {
      selected.clear();
      showAllModels();
      render();
      track('bedra_mattress_shortlist_cleared');
    });

    reset.addEventListener('click', () => {
      showAllModels();
      status.textContent = 'Showing all six mattresses.';
      track('bedra_mattress_comparison_reset');
    });

    render();
  };

  const setupFinder = () => {
    const form = hub.querySelector('[data-mh-finder]');
    const result = hub.querySelector('[data-mh-finder-result]');
    const cards = all('[data-mh-model]');
    if (!form || !result || !cards.length) return;

    const normalise = (value) => (value || '').toLowerCase().trim();
    const field = (data, key) => normalise(data[key]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const values = Object.fromEntries(new FormData(form).entries());

      cards.forEach((card) => card.classList.remove('is-recommended', 'is-alternative'));

      const eligible = cards.filter((card) => {
        if (!values.size) return true;
        const availableSizes = field(card.dataset, 'sizes')
          .split('|')
          .map((size) => size.replace(/\s+size$/, '').trim())
          .filter(Boolean);
        return availableSizes.includes(normalise(values.size));
      });

      if (!eligible.length) {
        result.replaceChildren();
        const title = document.createElement('h3');
        title.textContent = 'No core model currently shows that exact size.';
        const copy = document.createElement('p');
        copy.textContent = 'Try “Any size”, or view the wider range below. Confirm availability on the product page before ordering.';
        result.append(title, copy);
        result.hidden = false;
        track('bedra_mattress_finder_no_match', { requested_size: values.size || 'any' });
        result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      const scored = eligible.map((card, index) => {
        let score = Math.max(0, 6 - index) * .01;
        if (values.sleep && field(card.dataset, 'sleep').split(' ').includes(values.sleep)) score += 3;
        if (values.feel && field(card.dataset, 'feel') === values.feel) score += 3;
        if (values.cooling && field(card.dataset, 'cooling') === values.cooling) score += 2;
        if (values.partner && field(card.dataset, 'partner') === values.partner) score += 2;
        if (values.budget && field(card.dataset, 'budget') === values.budget) score += 2;
        return { card, score };
      }).sort((a, b) => b.score - a.score);

      const primary = scored[0].card;
      const alternative = scored[1] ? scored[1].card : null;
      primary.classList.add('is-recommended');
      if (alternative) alternative.classList.add('is-alternative');

      result.replaceChildren();
      const kicker = document.createElement('p');
      kicker.className = 'mh-kicker';
      kicker.textContent = 'YOUR STARTING POINT';
      const title = document.createElement('h3');
      const mainLink = document.createElement('a');
      mainLink.href = primary.dataset.modelUrl;
      mainLink.textContent = primary.dataset.modelName;
      title.append(mainLink);
      const copy = document.createElement('p');
      copy.append('Begin here for ', document.createTextNode(primary.dataset.modelReason), '. This is guidance—not a guarantee of personal comfort.');
      result.append(kicker, title, copy);

      if (alternative) {
        const alternatives = document.createElement('div');
        alternatives.className = 'mh-finder-result__alternatives';
        const label = document.createElement('span');
        label.textContent = 'Also compare:';
        const link = document.createElement('a');
        link.href = alternative.dataset.modelUrl;
        link.textContent = alternative.dataset.modelName;
        alternatives.append(label, link);
        result.append(alternatives);
      }

      result.hidden = false;
      track('bedra_mattress_finder_completed', {
        recommended_model: primary.dataset.modelKey,
        alternative_model: alternative ? alternative.dataset.modelKey : '',
        requested_size: values.size || 'any'
      });
      result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const setupTechnology = () => {
    const tabs = all('[data-mh-tech-tab]');
    const panels = all('[data-mh-tech-panel]');
    const image = hub.querySelector('[data-mh-tech-image]');
    if (!tabs.length || !panels.length || !image) return;

    tabs.forEach((tab, index) => {
      tab.id = `mh-tech-tab-${index}`;
      tab.setAttribute('aria-controls', `mh-tech-panel-${index}`);
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      if (panels[index]) {
        panels[index].id = `mh-tech-panel-${index}`;
        panels[index].setAttribute('role', 'tabpanel');
        panels[index].setAttribute('aria-labelledby', tab.id);
      }
    });

    const select = (tab, focus = false) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.mhTechPanel !== tab.dataset.tech; });
      image.src = tab.dataset.src;
      image.alt = tab.dataset.alt;
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        select(tab);
        track('bedra_mattress_construction_viewed', { construction: tab.dataset.tech });
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        select(tabs[next], true);
      });
    });
  };

  const setupSectionNavigation = () => {
    const nav = hub.querySelector('.mh-anchorbar');
    const links = all('.mh-anchorbar a');
    if (!nav || !links.length) return;
    const targets = links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);

    const update = () => {
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = documentHeight > 0 ? Math.min(100, Math.max(0, window.scrollY / documentHeight * 100)) : 0;
      nav.style.setProperty('--mh-progress', `${progress}%`);
      const offset = nav.offsetHeight + 90;
      let active = targets[0];
      targets.forEach((section) => { if (section.getBoundingClientRect().top <= offset) active = section; });
      links.forEach((link) => link.classList.toggle('is-active', active && link.getAttribute('href') === `#${active.id}`));
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  };

  const setupReveals = () => {
    const items = all('.mh-section-head, .mh-model-card, .mh-review-card, .mh-assurance-grid article');
    items.forEach((item) => item.setAttribute('data-mh-reveal', ''));
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    items.forEach((item) => observer.observe(item));
  };

  setupSizeSelection();
  setupShortlist();
  setupLinkTracking();
  setupFinder();
  setupTechnology();
  setupSectionNavigation();
  setupReveals();
})();
